import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { common } from "../../../../i18n";
import Grid2 from "@mui/material/Unstable_Grid2";

import { useExternalApiKeyStore } from "../../../../contexts/useExternalApiKeyStore";
import { CustomAutoComplete } from "../../../../components/input/CustomAutoComplete";
import { useForm } from "react-hook-form";
import { InputReactHookForm } from "../../../../components/input/InputReactHookForm";
import alertService from "../../../../components/alert/alertService";
import CopyableField from "../../../../components/input/CopyableField";

type CreateApiKeyForm = {
  serviceRef: string;
  description: string;
  consent?: boolean;
};

export const CreateApiKey = () => {
  const { services, createKey, setOpenModal } = useExternalApiKeyStore();

  const { t } = useTranslation([common], { nsMode: "fallback" });

  const {
    control,
    watch,
    setValue,
    handleSubmit,

    formState: { isSubmitting, isValid },
  } = useForm<CreateApiKeyForm>({
    defaultValues: {
      description: undefined,
      serviceRef: undefined,
      consent: false,
    },
  });

  const checked = watch("consent");

  async function onSubmit(formData: CreateApiKeyForm) {
    const { consent, ...dataWithOutConsent } = formData;

    const { description, ...dataWithOutDescription } = dataWithOutConsent;

    const key = await createKey(
      formData.description && formData.description !== ""
        ? dataWithOutConsent
        : dataWithOutDescription
    );

    if (key) {
      setOpenModal();

      alertService.confirm(
        t("API-Key"),
        <CopyableField value={key} />,
        undefined,
        undefined,
        false,
        undefined,
        undefined,
        undefined,
        false,
        true
      );
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <Grid2 container disableEqualOverflow rowSpacing={"20px"}>
        <Grid2 xs={12}>
          <Box
            display={"flex"}
            flexDirection={{ xs: "row", sm: "row" }}
            alignItems={"center"}
            gap={"16px"}
          >
            <Box flex={1}>
              <Typography variant="body1" color={"var(--gray1)"}>
                {`${t("Service-Name")}*`}
              </Typography>
            </Box>

            <Box flex={4}>
              <CustomAutoComplete
                name={"serviceRef"}
                control={control}
                label={""}
                info={""}
                placeholder={""}
                listMenuItem={
                  services.map((s) => ({
                    nameEN: s.name,
                    nameTH: s.name,
                    value: s.serviceRef,
                  })) ?? []
                }
                showLabelInput={false}
                rules={{
                  required: t("This-field-is-required"),
                }}
              />
            </Box>
          </Box>
        </Grid2>

        <Grid2 xs={12}>
          <Box
            display={"flex"}
            flexDirection={{ xs: "row", sm: "row" }}
            alignItems={"center"}
            gap={"16px"}
          >
            <Box flex={1}>
              <Typography variant="body1" color={"var(--gray1)"}>
                {`${t("Description")}`}
              </Typography>
            </Box>

            <Box flex={4}>
              <InputReactHookForm
                name="description"
                control={control}
                label={""}
                placeholder={`${t("Description")}`}
                showLabelInput={false}
              />
            </Box>
          </Box>
        </Grid2>
        <Grid2 xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                value={checked}
                onChange={(
                  event: ChangeEvent<HTMLInputElement>,
                  checked: boolean
                ) => setValue("consent", checked)}
                checked={checked}
                sx={{ ml: 0.3 }}
              />
            }
            label={t("API-Key-Acknowledgement")}
          />
        </Grid2>

        <Grid2 xs={12} textAlign={"right"}>
          <Button
            variant="contained"
            color="secondary"
            disabled={!checked || !isValid || isSubmitting}
            type="submit"
          >
            {t("Create-API-Key")}
          </Button>
        </Grid2>
      </Grid2>
    </form>
  );
};
